import React, { useEffect, useRef } from 'react';
import { isChildVisible } from './util';

const WithVisibilityObserver = ({
    children,
    onVisible,
    onVisibleAfter,
    visibleAfterMs = 1000,
    visibleAfterThreshold = 0.5,
    threshold,
    parent,
    ...props
}: React.PropsWithChildren<any> & {
    onVisible: (isVisibleOnLoad: boolean) => void;
    onVisibleAfter?: () => void;
    visibleAfterMs?: number;
    visibleAfterThreshold?: number;
    threshold: number;
    style?: object;
}) => {
    const componentRef = useRef(null);
    const hasFired = useRef(false); // State to track if the event has already fired
    const hasFiredAfter = useRef(false);
    useEffect(() => {
        if (
            hasFired.current ||
            !onVisible ||
            !componentRef.current ||
            !(componentRef.current as HTMLElement).parentNode
        ) {
            return;
        }

        const isVisible = isChildVisible(
            parent || ((componentRef.current as HTMLElement).parentNode as HTMLElement),
            componentRef.current
        );

        if (isVisible) {
            onVisible(true);
            hasFired.current = true;
        } else {
            // Check if IntersectionObserver is supported
            if ('IntersectionObserver' in window) {
                const observer = new IntersectionObserver(
                    (entries) => {
                        const entry = entries[0];
                        if (entry.isIntersecting && !hasFired.current) {
                            // Ensure the callback only fires once
                            hasFired.current = true;
                            onVisible(false);
                            observer.disconnect(); // Optionally disconnect observer after firing
                        }
                    },
                    {
                        root: null, // using the viewport as the root
                        rootMargin: '0px',
                        threshold, // 0.1 means trigger when at least 10% of the observed element is visible
                    }
                );

                if (componentRef.current) {
                    observer.observe(componentRef.current);
                }
            }
        }
    }, []); // Depend on hasFired to correctly handle its updates

    useEffect(() => {
        if (
            hasFiredAfter.current ||
            !onVisibleAfter ||
            !componentRef.current ||
            !('IntersectionObserver' in window)
        ) {
            return undefined;
        }

        let timer: number | undefined;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (
                    entry.isIntersecting &&
                    entry.intersectionRatio >= visibleAfterThreshold &&
                    !hasFiredAfter.current
                ) {
                    timer = window.setTimeout(() => {
                        if (hasFiredAfter.current) {
                            return;
                        }

                        hasFiredAfter.current = true;
                        onVisibleAfter();
                        observer.disconnect();
                    }, visibleAfterMs);
                    return;
                }

                if (timer) {
                    window.clearTimeout(timer);
                    timer = undefined;
                }
            },
            {
                root: null,
                rootMargin: '0px',
                threshold: [0, visibleAfterThreshold],
            }
        );

        observer.observe(componentRef.current);

        return () => {
            if (timer) {
                window.clearTimeout(timer);
            }
            observer.disconnect();
        };
    }, [onVisibleAfter, visibleAfterMs, visibleAfterThreshold]);

    // Wrap children in a div and apply the ref to the wrapper
    return (
        <div ref={componentRef} {...props}>
            {children}
        </div>
    );
};

export default WithVisibilityObserver;

// Usage example
/*
function App() {
  return (
    <WithVisibilityObserver onVisible={() => console.log('Child is now visible!')}>
      <div>Child Component</div>
    </WithVisibilityObserver>
  );
}
*/

// Explanation
/*
Why `hasFired` and `observer.disconnect()` Are Used Together:

- React's state updates (e.g., `setHasFired(true)`) are asynchronous. This means changes do not apply immediately, and React batches these updates for performance.
- The Intersection Observer's callback might fire multiple times in quick succession (e.g., when an element becomes visible then quickly not visible), before React processes state updates or re-renders.
- Using `observer.disconnect()` alone after the first invocation does not guarantee a one-off event because React may not have processed the `hasFired` state update in time, leading to potential multiple invocations.
- `hasFired` state ensures a one-off event by providing an immediate condition check within the callback, preventing multiple executions.
- This approach combines an immediate guard against repeated invocations (`hasFired`) with a method to stop future observations (`observer.disconnect()`), ensuring robust one-off visibility detection even amid rapid visibility changes and asynchronous state updates.
*/
